import React, { useCallback, useContext, useEffect, useState } from "react";
import Api from "../../utils/Api";
import { useUserStore } from "../../storez/UserStore";
import { useDateRangeFilterStore } from "../../storez/DateRangeFilterStore";
import "../../../css/ReportingBlocked.css";
import GlobalDatePickerVisibilityActions from "../../actions/GlobalDatePickerVisibilityActions";
import { Box, Button, Flex, Text } from "@familyzone/component-library";
import { ButtonGroup } from "@chakra-ui/button";
import { PieChart } from "../../components/PieChart";

import { useMount, useSetState, useUnmount } from "react-use";

import { ReactRouterCompatContext } from "../ContextProvider";

import ReportingSearchableTable from "./ReportingSearchableTable";
import PageWithHeader from "../templates/PageWithHeader";
import { fullNameOrUsername } from "../UserSearch/UserSearchHelper";
import { colors } from "../../theme";

export const UseContextProvider = () => useContext(ReactRouterCompatContext);

export const indexOptions = {
  users: "users",
  apps: "apps",
  policies: "policies",
};

export function findTitles(apiTrafficResult) {
  let data = [];
  let data_map = {};
  for (let appRow of apiTrafficResult) {
    let label = appRow["website"];
    if (appRow["tag"] !== appRow["categoryId"] && appRow["tag"] !== appRow["subCategoryId"]) {
      if (appRow["tag.string"]) {
        label = appRow["tag.string"];
      }
    }
    appRow.type_name = label;
    appRow.type = appRow["tag"];
    if (label) {
      if (!data_map[appRow["type_name"]]) {
        data_map[appRow["type_name"]] = appRow;
      } else {
        data_map[appRow["type_name"]]["hits"] += appRow["hits"];
      }
    }
  }
  data = Object.values(data_map);
  return data;
}

export function BlockedTable({ app_filtering, user_filtering, tab_update, date_changed, data_up_to_date }) {
  const [state, setState] = useSetState({
    formatted_apps: [],
    formatted_policies: [],
    formatted_users: [],
    selected_index: UseContextProvider()?.router?.location.query.startingIndex || indexOptions.users,
    app_loaded: false,
    policy_loaded: false,
    users_loaded: false,
    isFetching: false,
  });

  const data_loaded = !state.isFetching && (state.app_loaded || state.policy_loaded || state.users_loaded);

  const getUsersAsMap = useUserStore((state) => state.getUsersAsMap);

  const load_policies = useCallback(() => {
    setState({ isFetching: true });
    Api.get_analytics(
      "/surfwize/analytics/filtering/policies",
      function (result) {
        const policy_filtering = result["data"];
        if (policy_filtering) {
          for (let policy of policy_filtering) {
            policy["linkUrl"] = "/cybersafety/blocks/violations/" + policy["policy"] + "?&blocked=true&hits=true";
          }
        }
        setState({
          formatted_policies: policy_filtering,
          selected_index: indexOptions.policies,
          policy_loaded: true,
          isFetching: false,
        });
        tab_update(state.selected_index);
      },
      function (err) {
        console.log(err);
      }
    );
  }, [setState, state.selected_index, tab_update]);

  const load_apps = useCallback(() => {
    const appData = app_filtering; // Use the correct prop data passed from parent
    if (appData && appData.length > 0) {
      setState({ isFetching: true });
      const types_websites = [...appData]; // Create a copy to avoid mutations
      if (types_websites) {
        for (let app of types_websites) {
          if (app["type_name"] === app["domain"]) {
            app["linkUrl"] = "/surfwize/reporting/website/" + app["domain"] + "/users?&blocked=true&hits=true";
          } else {
            app["linkUrl"] = "/surfwize/reporting/type/" + app["type"] + "/users?&blocked=true&hits=true";
          }
        }
      }
      setState({
        formatted_apps: types_websites,
        selected_index: indexOptions.apps,
        app_loaded: true,
        isFetching: false,
      });
    } else {
      setState({
        formatted_apps: [],
        selected_index: indexOptions.apps,
        app_loaded: true,
        isFetching: false,
      });
    }
    tab_update(state.selected_index);
  }, [setState, state.selected_index, tab_update, app_filtering]);

  const loadFormattedUsers = useCallback(() => {
    if (user_filtering && user_filtering.length > 0) {
      setState({ isFetching: true });
      const userNames = user_filtering.map((userRow) => userRow.user);
      getUsersAsMap(userNames).then((users) => {
        let formattedUsers = [];
        for (let userRow of user_filtering) {
          const user = users.get(userRow["user"]);
          userRow["full_name"] = user ? fullNameOrUsername(user) : userRow["user"];
          userRow["linkUrl"] = "/surfwize/reporting/users/" + userRow["user"] + "/journey?verdict=Blocked";
          formattedUsers.push(userRow);
        }
        setState({
          formatted_users: formattedUsers,
          selected_index: indexOptions.users,
          users_loaded: true,
          isFetching: false,
        });
      });
    } else {
      setState({
        formatted_users: [],
        selected_index: indexOptions.users,
        users_loaded: true,
        isFetching: false,
      });
    }
    tab_update(state.selected_index);
  }, [getUsersAsMap, setState, user_filtering]);

  // React to date range changes
  useEffect(() => {
    if (date_changed) {
      // Reset loaded flags to force data reload
      setState({
        policy_loaded: false,
      });

      // Reload data for the currently selected tab
      if (state.selected_index === "policies") {
        load_policies();
      }
    }
  }, [date_changed]);

  useEffect(() => {
    if (date_changed && data_up_to_date) {
      // Reset loaded flags to force data reload
      setState({
        app_loaded: false,
        users_loaded: false,
      });

      // Reload data for the currently selected tab
      if (state.selected_index === "users") {
        loadFormattedUsers();
      }
      if (state.selected_index === "apps") {
        load_apps();
      }
    }
  }, [date_changed, data_up_to_date]);

  const handle_show_users = () => {
    if (state.selected_index === indexOptions.users) {
      return;
    }

    if (state.users_loaded) {
      setState({ selected_index: indexOptions.users });
      return;
    }

    loadFormattedUsers();
  };

  const handle_load_apps = () => {
    if (state.selected_index === indexOptions.apps) {
      return;
    }
    if (state.app_loaded) {
      setState({ selected_index: indexOptions.apps });
      return;
    }
    load_apps();
  };

  const handle_load_policies = () => {
    if (state.selected_index === indexOptions.policies) {
      return;
    }

    if (state.policy_loaded) {
      setState({ selected_index: indexOptions.policies });
      return;
    }

    load_policies();
  };

  // new UI
  const newUI_get_data_columns = (index) => {
    switch (index) {
      case "users":
        return newUserDataColumns;
      case "apps":
        return newAppDataColumns;
      case "policies":
        return newPolicyDataColumns;
      default:
        return [];
    }
  };

  const get_data = (index) => {
    switch (index) {
      case "users":
        return state.formatted_users;
      case "apps":
        return state.formatted_apps;
      case "policies":
        return state.formatted_policies;
      default:
        return [];
    }
  };
  // new UI
  const newUserDataColumns = [
    { name: "full_name", header: "Username", isLink: true },
    { name: "hits", header: "Hits" },
  ];
  // new UI
  const newAppDataColumns = [
    { name: "type_name", header: "Application", isLink: true },
    { name: "hits", header: "Hits" },
  ];
  // new UI
  const newPolicyDataColumns = [
    { name: "policy_name", header: "Policy", isLink: true },
    { name: "hits", header: "Hits" },
  ];

  const searchFilter = (row, searchTerm) => {
    return !searchTerm || row.username.includes(searchTerm);
  };
  // new UI
  const selectedButtonStyle = {
    _focus: { outline: "none", boxShadow: "none" },
    backgroundColor: colors.neutrals["500"],
    color: colors.brand["100"],
  };
  // new UI
  const defaultButtonStyle = {
    outline: "0",
    border: "1px",
    borderColor: colors.neutrals["50"],
    backgroundColor: colors.neutrals["0"],
  };
  // new UI
  const buttonGroup = () => {
    return (
      <ButtonGroup isAttached>
        <Button
          sx={state.selected_index === indexOptions.users ? selectedButtonStyle : defaultButtonStyle}
          borderTopRightRadius="0px"
          borderBottomRightRadius="0px"
          onClick={handle_show_users}
        >
          Users
        </Button>
        <Button
          sx={state.selected_index === indexOptions.apps ? selectedButtonStyle : defaultButtonStyle}
          borderRadius="0px"
          onClick={handle_load_apps}
        >
          Apps
        </Button>
        <Button
          sx={state.selected_index === indexOptions.policies ? selectedButtonStyle : defaultButtonStyle}
          borderTopLeftRadius="0px"
          borderBottomLeftRadius="0px"
          onClick={handle_load_policies}
        >
          Policies
        </Button>
      </ButtonGroup>
    );
  };

  return (
    <ReportingSearchableTable
      isInfiniteScroll
      data={get_data(state.selected_index)}
      loaded={data_loaded}
      columns={newUI_get_data_columns(state.selected_index)}
      searchFilter={searchFilter()}
      tableButtonGroup={buttonGroup()}
      initialSortColumn={newUI_get_data_columns(state.selected_index)[1].name}
      initialSortDirection={"desc"}
    />
  );
}

function BlockedApps({ data }) {
  return (
    <Box height="100%">
      <PieChart
        dataLoaded={data !== undefined}
        data={data}
        divname="amChart-pie_top_filtered_applications"
        name="App"
        points={"hits"}
        legend={true}
        chartName="apps"
        onClick={function (tag) {
          UseContextProvider().router.push("/surfwize/reporting/type/" + tag.tag + "/users?blocked=true");
        }}
      />
    </Box>
  );
}

function BlockedUsers({ data }) {
  const [state, setState] = useSetState({
    data: undefined,
  });
  const [topItems, setTopItems] = useState(null);

  const getUsersAsMap = useUserStore((state) => state.getUsersAsMap);

  const onChange = useCallback(() => {
    setState({
      data: state.data,
    });
  }, [setState, state.data]);

  useEffect(() => {
    onChange();
  }, [onChange]);

  useEffect(() => {
    async function fetchTopItems() {
      let tempTopItems = [];
      if (data) {
        const userNames = data.map((userRow) => userRow.user);
        const users = await getUsersAsMap(userNames);
        for (let item of data) {
          const user = users.get(item["user"]);
          tempTopItems.push({
            name: user ? fullNameOrUsername(user) : item["user"],
            y: item["hits"],
            user: item["user"],
          });
          if (tempTopItems.length === 6) {
            break;
          }
        }
      }

      // Update the state with the fetched data
      setTopItems(tempTopItems);
    }

    fetchTopItems();
  }, [data, getUsersAsMap]);

  return (
    <Box height="100%">
      <PieChart
        dataLoaded={topItems !== undefined}
        data={topItems}
        divname="amChart-pie_top_filtered_users"
        name="App"
        points={"hits"}
        legend={true}
        showPercentage={false}
        chartName="users"
        onClick={function (user) {
          UseContextProvider().router.push("/surfwize/reporting/types?blocked=true&filter_user=" + user.user);
        }}
      />
    </Box>
  );
}

export default function ReportingBlocked(props) {
  const [state, setState] = useSetState({
    user_data: undefined,
    application_data: undefined,
    types_websites: undefined,
    policy_filtering: undefined,
    user_data_loaded: false,
    current_tab: "users",
    date_changed: false,
  });

  const handleLoad = useCallback(() => {
    // Loading should only occur when the Filter has changed to a blocked state:
    let application_data = [];
    const application_graph_data = [];
    let user_data = [];

    /* Save the datepicker state in state */

    const applicationsPromise = new Promise(function (resolve, reject) {
      Api.get_analytics(
        "/surfwize/analytics/traffic/typesandwebsites?blocked=true",
        function (result) {
          application_data = findTitles(result.data);
          // Sort by hits in descending order
          application_data.sort((a, b) => b.hits - a.hits);
          if (application_data) {
            for (let item of application_data) {
              application_graph_data.push({
                name: item["type_name"],
                y: item["hits"],
                tag: item["type"],
              });

              if (application_graph_data.length === 6) {
                break;
              }
            }
            resolve(application_data, application_graph_data);
          }
        },
        function (err) {
          reject();
          console.log(err);
        }
      );
    });

    const userPromise = new Promise(function (resolve, reject) {
      Api.get_analytics(
        "/surfwize/analytics/filtering/users?blocked=true",
        function (result) {
          user_data = result["data"];
          resolve(user_data);
        },
        function (err) {
          reject();
          console.log(err);
        }
      );
    });

    Promise.all([userPromise, applicationsPromise]).then(function (values) {
      setState({
        application_data: values[1],
        application_graph_data: application_graph_data,
        user_data: user_data,
        user_data_loaded: true,
        data_up_to_date: true,
        date_changed: true,
      });
    });
  }, [setState]);

  const handleDateChange = useCallback(() => {
    handleLoad();
    setState({ date_changed: true, data_up_to_date: false });
  }, [handleLoad, setState]);

  useMount(() => {
    const unsubFilterStore = useDateRangeFilterStore.subscribe(handleDateChange);

    setTimeout(() => {
      GlobalDatePickerVisibilityActions.showGlobalDatePicker();
      handleLoad(); // Only call once on mount
    }, 0);

    return () => {
      unsubFilterStore();
    };
  });

  const tabUpdate = () => {
    setState({
      date_changed: false,
    });
  };

  return (
    <PageWithHeader
      breadcrumbs={[
        { title: "Cyber Safety", url: "/cybersafety", isActive: false },
        { title: "Blocked", isActive: true },
      ]}
      title="Blocked"
    >
      <Flex height="80%">
        <Box width="65%">
          <BlockedTable
            app_filtering={state.application_data ? state.application_data : []}
            user_filtering={state.user_data ? state.user_data : []}
            tab_update={tabUpdate}
            date_changed={state.date_changed}
            data_up_to_date={state.data_up_to_date}
          />
        </Box>

        <Flex flexBasis="35%" flexDir="column" gap="sp24" p="sp24">
          <Box minHeight="300px" maxHeight="400px" flex="0 0 auto" p="sp24" borderRadius="md" bgColor="neutrals.0">
            <Text color={colors.text.title} fontSize="16px" fontWeight="700" lineHeight="20.45px" mb="sp16">
              Top Filtered Applications
            </Text>
            <Box height="calc(100% - 40px)">
              <BlockedApps data={state.application_graph_data ? state.application_graph_data : undefined} />
            </Box>
          </Box>

          <Box minHeight="300px" maxHeight="400px" flex="0 0 auto" p="sp24" borderRadius="md" bgColor="neutrals.0">
            <Text color={colors.text.title} fontSize="16px" fontWeight="700" lineHeight="20.45px" mb="sp16">
              Top Filtered Users
            </Text>
            <Box height="calc(100% - 40px)">
              <BlockedUsers data={state.user_data ? state.user_data.slice(0, 6) : undefined} />
            </Box>
          </Box>
        </Flex>
      </Flex>
    </PageWithHeader>
  );
}
